import React, { useEffect, useState } from "react";
import { Alert, Box, Stack, Typography } from "@mui/material";
import { api } from "../../api/apiService";
import { cardSx, errorText } from "./fields";
import { ModuleGrid } from "./ModuleGrid";

type Tool = {
  id: string;
  name: string;
  description: string;
  modules?: string[];
};

function asTools(data: unknown): Tool[] {
  if (!Array.isArray(data)) return [];
  return data.filter((row): row is Tool => Boolean(row) && typeof row === "object" && typeof (row as Tool).id === "string");
}

export function ToolsList() {
  const [tools, setTools] = useState<Tool[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const load = async () => {
    const data = await api.get("/api/agent-studio/tools");
    setTools(asTools(data));
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await load();
      } catch (err) {
        if (!cancelled) setFormError(errorText(err, "Could not load tools."));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const saveModules = async (tool: Tool, next: string[]) => {
    setTools((prev) => prev.map((item) => (item.id === tool.id ? { ...item, modules: next } : item)));
    setSavingId(tool.id);
    setFormError(null);
    try {
      await api.put(`/api/agent-studio/tools/${tool.id}`, { modules: next });
    } catch (err) {
      setFormError(errorText(err, "Could not update where this tool is available."));
      try {
        await load();
      } catch {
        // The previous error already explains the failure.
      }
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6" sx={{ color: "#fafafa", fontWeight: 600 }}>
          Tools
        </Typography>
        <Typography variant="body2" sx={{ color: "#a1a1aa", mt: 0.5 }}>
          Check the modules where each tool should be available.
        </Typography>
      </Box>
      {formError && <Alert severity="error">{formError}</Alert>}
      {loading && (
        <Typography variant="body2" sx={{ color: "#71717a" }}>
          Loading tools…
        </Typography>
      )}
      {tools.map((tool) => (
        <Box key={tool.id} sx={cardSx}>
          <Typography sx={{ color: "#fafafa", fontWeight: 600 }}>{tool.name}</Typography>
          <Typography variant="body2" sx={{ color: "#a1a1aa", mt: 0.5, mb: 1.5 }}>
            {tool.description}
          </Typography>
          <ModuleGrid
            value={tool.modules || []}
            disabled={savingId === tool.id}
            onChange={(next) => void saveModules(tool, next)}
          />
        </Box>
      ))}
    </Stack>
  );
}
